import React from 'react';
import { API } from '../api';

/*
 * What a long job is doing, over the top of the panel doing it.
 *
 * Making a poster or writing a newsletter takes the best part of a minute.
 * Before this, the only thing that changed was a spinner on the button, and
 * the form underneath stayed live — so an admin could carry on editing a
 * brief that had already been sent, and watch their changes have no effect.
 *
 * Three things it is careful about. It covers the panel and nothing else, so
 * the header and tabs still work and nobody is trapped. The steps are the
 * real ones reported by the server, so when it stops it stops on the thing
 * that failed rather than on a cheerful message. And it can be dismissed:
 * the work is already paid for and finishes regardless, so "leave this
 * running" is an honest offer rather than a cancel pretending to be one.
 */

const fmtElapsed = ms => {
  const s = Math.floor(ms / 1000);
  return s < 60 ? s + 's' : Math.floor(s / 60) + 'm ' + (s % 60) + 's';
};

/** A job id this browser makes up, so the server can be asked about it. */
export const newJobId = () => 'j' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

function GeneratingOverlay({ jobId, endpoint, title, onDismiss, fallbackSteps = [], typical }) {
  const [state, setState] = React.useState(null);
  const [elapsed, setElapsed] = React.useState(0);

  /* Asked once a second. The request doing the work cannot report on itself —
     it has not answered yet — so progress comes alongside it. */
  React.useEffect(() => {
    if (!jobId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}${endpoint}/${jobId}`);
        if (r.ok && !stop) {
          const j = await r.json();
          if (!j.unknown) setState(j);
        }
      } catch {
        /* A missed poll is not worth telling anybody about; the next one is
           a second away and the real answer is the request still in flight. */
      }
    };
    tick();
    const poll = setInterval(tick, 1000);
    const clock = setInterval(() => setElapsed(e => e + 1000), 1000);
    return () => { stop = true; clearInterval(poll); clearInterval(clock); };
  }, [jobId, endpoint]);

  const steps = (state && state.steps) || fallbackSteps;
  const current = state ? state.current : 0;
  const failed = state && state.failed;
  const shown = state ? state.elapsedMs : elapsed;

  return (
    <div className="gen-overlay" role="status" aria-live="polite">
      <div className="gen-card">
        <div className="gen-head">
          <i className={`fa ${failed ? 'fa-exclamation-triangle' : 'fa-spinner fa-spin'}`}></i>
          <span>{failed ? 'It stopped' : title}</span>
          <span className="gen-clock">{fmtElapsed(shown)}</span>
        </div>

        <ol className="gen-steps">
          {steps.map((name, i) => {
            const done = i < current;
            const now = i === current && !failed;
            const stuck = i === current && failed;
            return (
              <li key={name} className={`gen-step${done ? ' is-done' : ''}${now ? ' is-now' : ''}${stuck ? ' is-stuck' : ''}`}>
                <i className={`fa ${done ? 'fa-check' : stuck ? 'fa-times' : now ? 'fa-circle-o-notch fa-spin' : 'fa-circle-thin'}`}></i>
                <span>{name}</span>
                {/* Almost the whole wait is one step — the model drawing or
                    writing — and the others go by in a tenth of a second. So
                    the useful thing is not which step it is on but whether
                    the time it is taking is normal. */}
                {now && typical && <em className="gen-typical">{typical}</em>}
              </li>
            );
          })}
        </ol>

        {failed ? (
          <p className="gen-note">{failed}</p>
        ) : (
          <p className="gen-note">
            Nothing here can be changed until it finishes. You can leave it running and
            carry on elsewhere — it will not be lost.
          </p>
        )}

        <button className="w3-button w3-border gen-dismiss" onClick={onDismiss}>
          {failed ? 'Close' : 'Leave it running'}
        </button>
      </div>
    </div>
  );
}

export default GeneratingOverlay;
